/**
 * Pure client-side rules for what the player is shown about THEIR money.
 * No DOM, no network — unit-tested in test/result.test.js.
 *
 * The server is the only authority on stakes and payouts. These helpers exist
 * so the client never invents a number:
 *   - mergeMyBets: the server's record of my stake + only the taps still in flight
 *   - isCurrentRound: lobby events about an OLD round must not hijack the live one
 *   - classifyBetFailure: retry (same nonce) vs. roll back
 *   - summarizeMyResult: the result sheet line, from GET /round/:id/my-result
 */

/**
 * My per-slot stake for a round: what the server has committed, plus taps that
 * are queued or in flight (not yet acknowledged). Acknowledged taps are already
 * inside the server figure and must not be added twice.
 */
export function mergeMyBets(serverMyBets, pendingTaps, roundId) {
  const out = {};
  for (const [slot, amount] of Object.entries(serverMyBets || {})) {
    const n = Math.floor(Number(amount));
    if (Number.isFinite(n) && n > 0) out[String(slot)] = n;
  }
  for (const tap of pendingTaps || []) {
    if (!tap || (roundId && tap.roundId !== roundId)) continue;
    const n = Math.floor(Number(tap.amount));
    if (!Number.isFinite(n) || n <= 0) continue;
    const key = String(tap.slotId);
    out[key] = (out[key] || 0) + n;
  }
  return out;
}

/** True when a lobby event concerns the round the player is looking at. */
export function isCurrentRound(eventRoundId, currentRoundId) {
  return !!eventRoundId && !!currentRoundId && String(eventRoundId) === String(currentRoundId);
}

/**
 * A bet request that may have reached the server must be RETRIED with the same
 * nonce (the server applies a nonce once), never rolled back on a guess.
 * Only a definitive refusal (4xx other than 408/429) is rolled back.
 */
export function classifyBetFailure(err) {
  const status = err && err.status;
  if (!status) return 'retry';             // network error / no response
  if (status >= 500) return 'retry';
  if (status === 408 || status === 429) return 'retry';
  return 'reject';
}

/**
 * The player's line on the result sheet, from the server's my-result payload.
 * @returns {{ kind: 'win'|'lose'|'none'|'pending'|'review'|'refunded', totalBet, totalPayout, net }}
 */
export function summarizeMyResult(result) {
  if (!result) return { kind: 'pending', totalBet: 0, totalPayout: 0, net: 0 };
  const totalBet = Math.max(0, Math.floor(Number(result.totalBet || 0)));
  const totalPayout = Math.max(0, Math.floor(Number(result.totalPayout || 0)));
  const net = Math.floor(Number(result.net ?? (totalPayout - totalBet)));
  if (totalBet === 0) return { kind: 'none', totalBet, totalPayout, net: 0 };
  if (result.voided) return { kind: 'refunded', totalBet, totalPayout, net };
  if (!result.settled) return { kind: 'pending', totalBet, totalPayout, net };
  const bets = Array.isArray(result.bets) ? result.bets : [];
  if (bets.some((b) => b.won && b.creditFailed)) return { kind: 'review', totalBet, totalPayout, net };
  if (totalPayout > 0) return { kind: 'win', totalBet, totalPayout, net };
  return { kind: 'lose', totalBet, totalPayout, net };
}

/** Client-side status for a server round status ('closed' is a brief pre-spin lock). */
export function clientStatusFor(serverStatus) {
  switch (serverStatus) {
    case 'betting': return 'betting';
    case 'closed':
    case 'spinning': return 'spinning';
    case 'finished': return 'result';
    default: return 'idle';
  }
}
